import styled from "styled-components";
import {
  Avatar,
  Button,
  Card,
  Flex,
  Form,
  Input,
  Layout,
  Modal,
  Spin,
  Typography,
  Upload,
} from "antd";
import { COLORS } from "../../../utils/colors";

const { Content } = Layout;
const { Title, Text } = Typography;
const { Password, OTP } = Input;
const { Item } = Form;

export const PageLayout = styled(Layout)`
  && {
    height: 100vh;
    background: linear-gradient(
      180deg,
      ${COLORS.pageGradientStart} 0%,
      ${COLORS.pageGradientEnd} 100%
    );
    font-family: "Roboto", sans-serif;
  }
`;

export const InnerLayout = styled(Layout)`
  && {
    background: transparent;
    height: 100vh;
    overflow-y: auto;
  }
`;

export const PageContent = styled(Content)`
  && {
    padding: 8px 28px 28px;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }

  @media (max-width: 575px) {
    && {
      padding: 8px 16px 16px;
    }
  }
`;

export const WelcomeBlock = styled(Flex)`
  && {
    margin-bottom: 22px;
  }
`;

export const WelcomeTitle = styled(Title)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 28px !important;
    font-weight: 700 !important;
    color: ${COLORS.textBody} !important;
    margin: 0 !important;
    line-height: 36px !important;
  }

  @media (max-width: 575px) {
    && {
      font-size: 22px !important;
      line-height: 28px !important;
    }
  }
`;

export const WelcomeSubtitle = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    color: ${COLORS.textMuted};
  }
`;

export const SettingsForm = styled(Form)`
  && {
    width: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
` as unknown as typeof Form;

export const SectionCard = styled(Card)`
  && {
    border: 1px solid ${COLORS.borderCard};
    border-radius: 16px;
    background: ${COLORS.surface};
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
    margin-bottom: 22px;
  }

  && .ant-card-body {
    padding: 24px 26px;
  }
`;

export const CardHeading = styled(Flex)`
  && {
    margin-bottom: 22px;
  }
`;

export const CardTitle = styled(Title)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 17px !important;
    font-weight: 700 !important;
    color: ${COLORS.textBody} !important;
    margin: 0 !important;
    line-height: 24px !important;
  }
`;

export const CardSubtitle = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    color: ${COLORS.textMuted};
  }
`;

export const AvatarBlock = styled(Flex)`
  && {
    min-width: 0;
  }
`;

export const AvatarUpload = styled(Upload)`
  && .ant-upload {
    cursor: pointer;
  }
`;

export const ProfileAvatar = styled(Avatar)`
  && {
    background: ${COLORS.surfaceSelected};
    color: ${COLORS.primary};
    font-family: "Roboto", sans-serif;
    font-size: 22px;
    font-weight: 600;
    flex-shrink: 0;
    border: 2px solid ${COLORS.surface};
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.12);
  }
`;

export const AvatarInfo = styled(Flex)`
  && {
    min-width: 0;
  }
`;

export const AvatarName = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: ${COLORS.textBody};
    line-height: 18px;
  }
`;

export const AvatarEmail = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    color: ${COLORS.textMuted};
    line-height: 16px;
    word-break: break-word;
  }
`;

export const AvatarHint = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 11px;
    color: ${COLORS.textSubtle};
    line-height: 15px;
  }
`;

export const AvatarRemoveLink = styled(Typography.Link)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: ${COLORS.unsuccessText};
    margin-top: 2px;
    width: fit-content;
  }

  &&:hover {
    color: ${COLORS.unsuccessText};
    opacity: 0.8;
  }
`;

export const FormItem = styled(Item)`
  && {
    margin-bottom: 0;
  }

  && .ant-form-item-label {
    padding-bottom: 6px;
  }

  && .ant-form-item-label > label {
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: ${COLORS.textMuted};
    height: auto;
  }

  && .ant-form-item-label > label::after {
    display: none;
  }
` as unknown as typeof Item;

const fieldStyles = `
  height: 44px;
  border-radius: 10px;
  border: 1px solid ${COLORS.border};
  background: ${COLORS.surface};
  font-family: "Roboto", sans-serif;
  font-size: 13px;
  color: ${COLORS.textBody};
  box-shadow: none;

  &:hover,
  &:focus,
  &:focus-within {
    border-color: ${COLORS.primary};
    box-shadow: none;
  }
`;

export const ReadonlyField = styled(Flex)`
  && {
    ${fieldStyles}
    padding: 0 14px;
    align-items: center;
    cursor: default;
    user-select: none;
    -webkit-user-select: none;
  }

  &&:hover {
    border-color: ${COLORS.border};
  }
`;

export const ReadonlyFieldLabel = styled(Text)`
  && {
    display: block;
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: ${COLORS.textMuted};
    margin-bottom: 6px;
  }
`;

export const ReadonlyValue = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    color: ${COLORS.textBody};
    user-select: none;
    -webkit-user-select: none;
    pointer-events: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const DisabledFieldWrap = styled.span`
  display: block;
  width: 100%;
  cursor: not-allowed;

  && .ant-input-affix-wrapper,
  && .ant-input {
    pointer-events: none;
  }
`;

export const PasswordField = styled(Password)`
  && {
    ${fieldStyles}
    padding: 0 14px;
  }

  && .ant-input {
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    color: ${COLORS.textBody};
    background: transparent;
  }

  && .ant-input-suffix .anticon {
    color: ${COLORS.textSubtle};
    font-size: 15px;
  }
`;

export const FooterRow = styled(Flex)`
  && {
    margin-top: auto;
    padding-top: 22px;
  }

  @media (max-width: 575px) {
    && {
      flex-direction: column-reverse;
    }
  }
`;

export const CancelButton = styled(Button)`
  && {
    height: 42px;
    padding: 0 24px;
    border-radius: 10px;
    border: 1px solid ${COLORS.border};
    background: ${COLORS.surface};
    color: ${COLORS.textBody};
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    font-weight: 500;
    box-shadow: none;

    &:hover,
    &:focus {
      background: ${COLORS.surface} !important;
      color: ${COLORS.textBody} !important;
      border-color: ${COLORS.primary} !important;
    }
  }

  @media (max-width: 575px) {
    && {
      width: 100%;
    }
  }
`;

export const SaveButton = styled(Button)`
  && {
    height: 42px;
    padding: 0 24px;
    border-radius: 10px;
    background: ${COLORS.primary};
    border: 1px solid ${COLORS.primary};
    color: ${COLORS.textOnPrimary};
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    font-weight: 600;
    box-shadow: none;

    &:hover,
    &:focus {
      background: ${COLORS.primaryHover} !important;
      border-color: ${COLORS.primaryHover} !important;
      color: ${COLORS.textOnPrimary} !important;
    }
  }

  && .anticon {
    font-size: 15px;
  }

  @media (max-width: 575px) {
    && {
      width: 100%;
    }
  }
`;

export const EditableField = styled(Input)`
  && {
    height: 44px;
    border-radius: 10px;
    border: 1px solid ${COLORS.border};
    background: ${COLORS.surface};
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    color: ${COLORS.textBody};
    box-shadow: none;
    padding: 0 14px;

    &:hover,
    &:focus {
      border-color: ${COLORS.primary};
      box-shadow: none;
    }
  }
`;

export const AvatarSpinWrapper = styled(Spin)`
  && {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.6);
  }
`;

export const AvatarWrapper = styled(Flex)`
  && {
    position: relative;
    flex-shrink: 0;
    width: 64px;
    height: 64px;
  }
`;

export const VerifyOtpInput = styled(OTP)`
  && {
    width: 100%;
    display: flex;
    gap: 10px;
  }

  && .ant-otp-input-wrapper {
    flex: 1 1 0;
    min-width: 0;
  }

  && .ant-otp-input {
    width: 100%;
    min-width: 0;
    height: 52px;
    aspect-ratio: auto;
    padding: 0 4px;
    border: 1px solid ${COLORS.border};
    border-radius: 999px;
    background: ${COLORS.surface};
    font-family: "Roboto", sans-serif;
    font-size: 20px;
    font-weight: 600;
    color: ${COLORS.textBody};
    text-align: center;
    box-shadow: none;

    &:hover,
    &:focus {
      border-color: ${COLORS.primary};
      box-shadow: none;
    }
  }

  @media (max-width: 575px) {
    && {
      gap: 6px;
    }

    && .ant-otp-input {
      height: 42px;
      font-size: 16px;
    }
  }
`;

export const VerifyModal = styled(Modal)`
  && .ant-modal-content {
    border-radius: 16px;
    padding: 32px 28px;
  }

  && .ant-modal-title {
    font-family: "Roboto", sans-serif;
    font-size: 17px;
    font-weight: 700;
    color: ${COLORS.textBody};
  }
`;

export const VerifyModalHint = styled(Typography.Text)`
  && {
    display: block;
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    color: ${COLORS.textMuted};
    margin-bottom: 20px;
  }
`;

export const VerifyModalFooter = styled(Flex)`
  && {
    margin-top: 24px;
  }
`;

export const VerifySubmitButton = styled(Button)`
  && {
    height: 42px;
    padding: 0 28px;
    border-radius: 10px;
    background: ${COLORS.primary};
    border: 1px solid ${COLORS.primary};
    color: ${COLORS.textOnPrimary};
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    font-weight: 600;
    box-shadow: none;

    &:hover,
    &:focus {
      background: ${COLORS.primaryHover} !important;
      border-color: ${COLORS.primaryHover} !important;
      color: ${COLORS.textOnPrimary} !important;
    }
  }
`;

export const VerifyCancelButton = styled(Button)`
  && {
    height: 42px;
    padding: 0 24px;
    border-radius: 10px;
    border: 1px solid ${COLORS.border};
    background: ${COLORS.surface};
    color: ${COLORS.textBody};
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    font-weight: 500;
    box-shadow: none;

    &:hover,
    &:focus {
      background: ${COLORS.surface} !important;
      color: ${COLORS.textBody} !important;
      border-color: ${COLORS.primary} !important;
    }
  }
`;
